import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /** Quantity change handler */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) return this.onLineItemRemove(line);

    this.updateQuantity({ line, quantity, action: 'change' });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = lineItemRow.querySelector('text-component');
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = JSON.parse(responseText);
        resetShimmer(this);
        if (parsed.errors) return this.#handleCartError(line, parsed);

        morphSection(this.sectionId, parsed.sections[this.sectionId]);
        this.#updateQuantitySelectors(parsed);
        this.#updateCartQuantitySelectorButtonStates();

        // NEW: Update free shipping & suggested products
        this.updateSideCartExtras(parsed.cart || cart);
      })
      .catch(console.error)
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsed) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');
    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsed.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // NEW: Update free shipping & suggested products
    this.updateSideCartExtras(event.detail.data.cart || cart);
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      selector.updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  /** NEW: Update free shipping bar and suggested products */
  updateSideCartExtras(cart) {
    if (!cart) return;

    // --- Free shipping bar ---
    const freeShippingThreshold = 100;
    const cartTotal = cart.total_price / 100;
    let progress = (cartTotal / freeShippingThreshold) * 100;
    if (progress > 100) progress = 100;

    const progressBar = this.querySelector('.free-shipping-fill');
    if (progressBar) progressBar.style.width = progress + '%';

    const freeShippingText = this.querySelector('.free-shipping-text');
    if (freeShippingText) {
      if (cartTotal >= freeShippingThreshold) {
        freeShippingText.textContent = "🎉 You’ve qualified for free shipping!";
      } else {
        const remaining = (freeShippingThreshold - cartTotal).toFixed(2);
        freeShippingText.textContent = `Spend $${remaining} more for free shipping`;
      }
    }

    const totalEl = this.querySelector('.cart-total');
    if (totalEl) totalEl.textContent = `Total: $${cartTotal.toFixed(2)}`;

    // --- Suggested products carousel ---
    const suggestedContainer = this.querySelector('.suggested-products-carousel');
    if (suggestedContainer) {
      suggestedContainer.innerHTML = '';
      const suggestedProducts = [
        { title: "Product 1", price: "$25", url: "#", img: "/path/image1.jpg" },
        { title: "Product 2", price: "$30", url: "#", img: "/path/image2.jpg" },
        { title: "Product 3", price: "$45", url: "#", img: "/path/image3.jpg" },
        { title: "Product 4", price: "$20", url: "#", img: "/path/image4.jpg" }
      ];

      suggestedProducts.forEach(product => {
        const card = document.createElement('div');
        card.className = 'suggested-product-card';
        card.innerHTML = `
          <a href="${product.url}">
            <img src="${product.img}" alt="${product.title}" />
            <p>${product.title}</p>
            <p>${product.price}</p>
          </a>
        `;
        suggestedContainer.appendChild(card);
      });
    }
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
